import {
  percentageFormat,
  totalCostFormat,
} from '../../reusable/reusableFunctions.js';
import { getMoneyObj } from '../../reusable/MoneyJsHelpers.js';
import {
  BOX_CHOICE_TYPES,
  BUDGETER_COST_MANIPULATOR_MODES,
  PERSONNEL_OBJECT_TYPE_ID,
  QUESTION_TYPE_IDS,
  TABLE_COLUMN_WIDTH,
} from '../../../static/constants.js';
import { REQUEST_TYPES_DB } from '../budgetRequestsHelpers.js';
import { messageManager } from '../../System/messageManager/messageManager.js';
import { EXPENSE, PERSONNEL, PROGRAM, PROGRAM_DEPARTMENT, PROGRAM_DIVISION, USER_GROUP } from '../../../static/keyWords.js';
import { createRequestStepOneDisablingCondition, createRequestStepZeroDisablingCondition } from './nextButtonDisablingConditions.js';
import { FILTER_TYPES } from '../../reusable/Filters/filterHelpers.js';
import { customAggregateFunction } from '../../System/AgGrid/RegisterAgGridModules.js';

export const MANUAL_ALLOCATION = 'Manual allocation';
export const STEPS = {
  increase: [
    { label: 'Increase' },
    { label: 'Allocation' },
    { label: 'Questions' },
    { label: 'Review' },
  ],
  decrease: [
    { label: 'Decrease' },
    { label: 'Allocation' },
    { label: 'Questions' },
    { label: 'Review' },
  ],
  ['add-existing']: [
    { label: 'Details' },
    { label: 'Allocation' },
    { label: 'Questions' },
    { label: 'Review' },
  ],
};

export const QUESTION_ANSWERS_ACCEPTED_FILES = [
  '.csv',
  '.xlsx',
  '.pdf',
  '.docx',
  '.pptx',
  '.png',
  '.jpg',
];

export const ALLOCATION_TYPE_TABS = ['Manual allocation', 'Auto allocate'];

export const prepareRequestBody = (data, requestType, activeBudget) => {
  const request = {
    budgetId: activeBudget?.id?.toString(),
    type: requestType,
    rawType:
      requestType === REQUEST_TYPES_DB.CREATE_NEW_ITEM_FROM_EXISTING ?
        'New'
        : requestType.charAt(0).toUpperCase() +
        requestType.slice(1).toLowerCase(),
    oldTotalCost:
      requestType === REQUEST_TYPES_DB.CREATE_NEW_ITEM_FROM_EXISTING ?
        0
        : Number(data.totalCost),
    newTotalCost: Number(data.totalCost),
    originalTotalCost: Number(data.totalCost),
    requestedAmount: data.newTotalCost - data.oldTotalCost,
    oldNumberOfItems: data.numberOfItems,
    newNumberOfItems: data.numberOfItems,
    description: '',
    allocations: data.allocations.map((allocation) => {
      return {
        ...allocation,
        id:
          requestType === REQUEST_TYPES_DB.CREATE_NEW_ITEM_FROM_EXISTING ?
            null
            : allocation.id,
        currentProgramCost:
          requestType === REQUEST_TYPES_DB.CREATE_NEW_ITEM_FROM_EXISTING ?
            0
            : Number(allocation.currentProgramCost),
        currentPercentage:
          requestType === REQUEST_TYPES_DB.CREATE_NEW_ITEM_FROM_EXISTING ?
            0
            : Number(allocation.currentPercentage),
        proposedProgramCost: Number(allocation.proposedProgramCost),
        proposedPercentage: Number(allocation.proposedPercentage),
        requestedAmount:
          requestType === REQUEST_TYPES_DB.CREATE_NEW_ITEM_FROM_EXISTING ?
            Number(allocation.proposedProgramCost)
            : 0,
      };
    }),
    originalAllocations: data.allocations.map((allocation) => {
      return {
        ...allocation,
        currentProgramCost: Number(allocation.currentProgramCost),
        currentPercentage: Number(allocation.currentPercentage),
        proposedProgramCost: Number(allocation.proposedProgramCost),
        proposedPercentage: Number(allocation.proposedPercentage),
        requestedAmount: Number(allocation.requestedAmount),
      };
    }),
    selectedAllocationTab:
      requestType === 'decrease' ?
        ALLOCATION_TYPE_TABS[1]
        : ALLOCATION_TYPE_TABS[0],
    itemData: {
      itemId: data.id,
      numberOfItems: data.numberOfItems,
      costModelId: data.costModelId,
      durationTypeId: data.durationTypeId,
      durationType: data.durationType === 1 ? 'One time' : 'Ongoing',
      costCenterId: data.costCenterId,
      costCenter: data.costCenter.name,
      accountTypeId: data.account.accountTypeId,
      accountType: data.account.accountTypeId === 2 ? 'Expense' : 'Revenue',
      fundId: data.account.fundId,
      fund: data.account.fund.name,
      objectTypeId: data.account.objectTypeId,
      objectType:
        data.account.objectTypeId === 1 ? 'Personnel' : 'NonPersonnel',
      accountCode: data.account.code,
      objectCategories: data.account.objectCategoriesJson.map((oc) => {
        return {
          name: oc.name,
          categoryNumber: oc?.number?.toString(),
          objectCategoryTypeId: oc.objectCategoryTypeId,
          typeName: oc.typeName,
        };
      }),
      organizationLevels: data.account.organizationLevelsJson.map((ol) => {
        return {
          name: ol.name,
          organizationLevelTypeId: ol.organizationLevelTypeId,
          typeName: ol.typeName,
        };
      }),
      itemMeta1: data.itemMeta1,
      itemMeta2: data.itemMeta2,
      itemMeta3: data.itemMeta3,
      nameMeta: data.nameMeta,
      comments: data.comments,
    },
    questionnaire: normalizeQuestionnaire(
      data.questionnaire,
      data.account.objectTypeId,
    ),
    additionalQuestion: data.additionalQuestion,
    boxChoices: normalizeBoxChoices(data.boxChoices),
    requestId: null,
    isOffset: null,
  };
  return {
    ...request,
    requestedAmount: Math.abs(request.newTotalCost - request.oldTotalCost),
  };
};

const normalizeQuestionnaire = (questionnaire, objectTypeId) => {
  return questionnaire
    .map((qType) => qType.questions)
    .flat()
    .map((q) => {
      return {
        questionId: q.id,
        questionTypeId: q.questionTypeId,
        answer: (q.answers || []).length > 0 ? q.answers[0]?.answer : '',
        questionAnswerId: (q.answers || []).length > 0 ? q.answers[0]?.id : null,
        questionName: q.name,
        required: q.isRequired,
        questionTypeName: questionnaire.find((qt) => qt.id === q.questionTypeId)
          ?.name,
        type: q.isFile ? 'file' : 'text',
        fileLink: (q.answers || []).length > 0 ? q.answers[0]?.fileLink : null,
      };
    })
    .filter((q) =>
      objectTypeId === PERSONNEL_OBJECT_TYPE_ID ?
        q.questionTypeId !== QUESTION_TYPE_IDS.nonPersonnelQuestions
        : q.questionTypeId !== QUESTION_TYPE_IDS.personnelQuestions,
    )
    .sort((a, b) => a.questionTypeId - b.questionTypeId);
};

const normalizeBoxChoices = (boxChoices, boxTypes) => {
  return boxChoices
    .map((boxChoice) => boxChoice.boxChoice)
    .flat()
    .map((choice) => {
      return {
        boxChoiceTypeId: choice.boxChoiceTypeId,
        id: choice.id,
        name: choice.name,
        boxChoiceTypeName: boxChoices.find(
          (bc) => bc.id === choice.boxChoiceTypeId,
        )?.name,
        type: boxChoices.find((bc) => bc.id === choice.boxChoiceTypeId)?.type,
        selected:
          (boxTypes || [])
            .map((bt) => bt.selected)
            .map((selected) => selected.id)
            .includes(choice.id) ||
          choice.selected ||
          false,
        required: boxChoices.find((bc) => bc.id === choice.boxChoiceTypeId)
          ?.isRequired,
      };
    });
};

export const prepareRequestBodyForEditFlow = (data, activeBudget) => {
  const objectCategories = Object.entries(data)
    .filter(
      ([, value]) => value && Object.hasOwn(value, 'objectCategoryTypeId'),
    )
    .map(([key, value]) => {
      return {
        typeName: key,
        ...value,
      };
    });

  const organizationLevels = Object.entries(data)
    .filter(
      ([, value]) => value && Object.hasOwn(value, 'organizationLevelTypeId'),
    )
    .map(([key, value]) => {
      return {
        typeName: key,
        ...value,
      };
    });

  const request = {
    budgetId: activeBudget.id,
    type: data.requestType,
    rawType:
      data.requestType === 'add-existing' ?
        'New'
        : data.requestType.charAt(0).toUpperCase() +
        data.requestType.slice(1).toLowerCase(),
    originalTotalCost: Number(data.totalCost),
    oldTotalCost:
      data.requestType === 'add-existing' ? 0 : Number(data.oldTotalCost),
    newTotalCost: Number(data.totalCost),
    requestedAmount: Number(data.requestedAmount),
    oldNumberOfItems: data.oldNumberOfItems,
    newNumberOfItems: data.numberOfItems,
    description: data.description,
    allocations: data.allocations,
    originalAllocations: data.allocations,
    selectedAllocationTab:
      data.requestType === 'decrease' ?
        ALLOCATION_TYPE_TABS[1]
        : ALLOCATION_TYPE_TABS[0],
    itemData: {
      itemId: data.itemId,
      numberOfItems: data.numberOfItems,
      costModelId: data.costModelId,
      durationTypeId: data.requestDurationType.id,
      durationType: data.requestDurationType.name,
      costCenterId: data['User Group'].id,
      costCenter: data['User Group'].name,
      accountTypeId: data.accountType.id,
      accountType: data.accountType.name,
      fundId: data.Funds.id,
      fund: data.Funds.name,
      objectTypeId: data.objectTypeId,
      objectType: data.costType,
      accountCode: data.accountCode,
      objectCategories: objectCategories,
      organizationLevels: organizationLevels,
      itemMeta1: data.itemMeta1,
      itemMeta2: data.itemMeta2,
      itemMeta3: data.itemMeta3,
      nameMeta: data.nameMeta,
      comments: data.comments,
    },
    questionnaire: normalizeQuestionnaire(
      data.questionnaire,
      data.objectTypeId,
    ),
    boxChoices: normalizeBoxChoices(data.boxChoices),
    requestId: data.itemRequestId,
    isOffset: data.isOffset,
  };

  return request;
};

export const prepareRequestForSaving = (request) => {
  return {
    ...request,
    budgetId: request?.budgetId ? String(request.budgetId) : '',
    allocations: (request.allocations || []).map((allocation) => {
      return {
        ...allocation,
        proposedPercentage: Number(allocation.proposedPercentage),
      };
    }),
    boxChoices: (request.boxChoices || []).filter(
      (boxChoice) => boxChoice.selected,
    ),
  };
};

export const prepareUploadedFiles = (questionAnswers) => {
  const fileAnswers = (questionAnswers || []).filter((q) => q.type === 'file');

  const result = {};

  fileAnswers.forEach((a) => {
    result[a.questionId] = {
      answerId: a.questionAnswerId,
      questionId: a.questionId,
      fileName: a.answer,
      name: a.answer,
      fileLink: a.fileLink,
    };
  });

  return result;
};

export const prepareUploadedFiles2 = (questions) => {
  // TODO: proper function naming
  const result = {};
  const fileAnswers = [];
  (questions.questionAnswers || []).forEach((questionGroup) =>
    questionGroup.questions.forEach((q) => {
      if (q.isFile) {
        fileAnswers.push({
          questionId: q.id,
          questionAnswerId: q.answers[0]?.id,
          answer: q.answers[0]?.answer,
          fileLink: q.answers[0]?.fileLink,
        });
      }
    }),
  );

  fileAnswers.forEach((a) => {
    result[a.questionId] = {
      answerId: a.questionAnswerId,
      questionId: a.questionId,
      fileName: a.answer,
      name: a.answer,
      fileLink: a.fileLink,
    };
  });

  return result;
};

export const FIELD_TYPE_NUMBER = 'number';
export const FIELD_TYPE_MONEY = 'money';
export const FIELD_TYPE_STRING = 'string';

const generateOrganizationLevelFields = (organizationLevels) => {
  return organizationLevels.map((ol, i) => {
    return {
      label: ol.typeName,
      propPath: `itemData.organizationLevels[${i}].name`,
      type: FIELD_TYPE_STRING,
    };
  });
};

const generateObjectCategoryFields = (objectCategories) => {
  return objectCategories.map((oc, i) => {
    return {
      label: oc.typeName,
      propPath: `itemData.objectCategories[${i}].name`,
      type: FIELD_TYPE_STRING,
    };
  });
};

export const getItemInfoFields = (request) => {
  let organizationLevelFields = generateOrganizationLevelFields(
    request.itemData.organizationLevels,
  );

  let objectCategoryFields = generateObjectCategoryFields(
    request.itemData.objectCategories,
  );

  return [
    {
      label: 'Cost type',
      propPath: 'itemData.objectType',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'Duration type',
      propPath: 'itemData.durationType',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'AcctCode',
      propPath: 'itemData.accountCode',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'Account type',
      propPath: 'itemData.accountType',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'Fund',
      propPath: 'itemData.fund',
      type: FIELD_TYPE_STRING,
    },
    ...organizationLevelFields,
    ...objectCategoryFields,
    {
      label: 'Old total item cost',
      propPath: 'oldTotalCost',
      type: FIELD_TYPE_MONEY,
    },
    {
      label: 'New total item cost',
      propPath: 'newTotalCost',
      type: FIELD_TYPE_MONEY,
    },
    {
      label: 'Old number of items',
      propPath: 'oldNumberOfItems',
      type: FIELD_TYPE_NUMBER,
    },
    {
      label: 'New number of items',
      propPath: 'newNumberOfItems',
      type: FIELD_TYPE_NUMBER,
    },
  ];
};

export const getSummaryItemInfoFields = (request, itemInfo) => {
  let orgLevelOne = request.itemData.organizationLevels.map((ol, i) => {
    return {
      label: ol.typeName,
      propPath: `itemData.organizationLevels[${i}].name`,
      type: FIELD_TYPE_STRING,
    };
  })[0];

  const fields = [
    orgLevelOne,
    {
      label: 'Cost type',
      propPath: 'itemData.objectType',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'Item duration',
      propPath: 'itemData.durationType',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'Total item cost',
      propPath: 'oldTotalCost',
      type: FIELD_TYPE_MONEY,
    },
    {
      label: 'Requested',
      propPath: 'requestedAmount',
      type: FIELD_TYPE_MONEY,
    },
    {
      label: 'New total item cost',
      propPath: 'newTotalCost',
      type: FIELD_TYPE_MONEY,
    },
  ];

  return itemInfo ? fields.slice(0, 3) : fields.slice(3, 6);
};

export const getFTEManipulatorToggleButtons = (requestType) => {
  if (requestType === 'increase') {
    return ['Increase by FTE', 'Increase by new total FTE'];
  } else {
    return ['Decrease by FTE', 'Decrease by new total FTE'];
  }
};

export const constructFTEFieldTitle = (requestType, selectedTab) => {
  if (requestType === 'increase') {
    return selectedTab === 'Increase by FTE' ? 'Increase by FTE' : (
      'Increase by new total FTE'
    );
  } else {
    return selectedTab === 'Decrease by FTE' ? 'Decrease by FTE' : (
      'Decrease by new total FTE'
    );
  }
};

export const determineFTEFieldValue = (request, selectedTab) => {
  if (selectedTab === 'Increase by FTE' || selectedTab === 'Decrease by FTE') {
    return request.newNumberOfItems - request.oldNumberOfItems;
  } else {
    return request.newNumberOfItems;
  }
};

export const setNewTotalFTE = (value, request, setRequest, selectedTab) => {
  let newTotalFTE;
  if (selectedTab === 'Increase by FTE') {
    newTotalFTE = request.oldNumberOfItems + value;
  } else if (selectedTab === 'Decrease by FTE') {
    if (value > request.oldNumberOfItems) {
      newTotalFTE = 0;
    } else {
      newTotalFTE = request.oldNumberOfItems - value;
    }
  } else if (selectedTab === 'Increase by new total FTE') {
    newTotalFTE = value;
  } else if (selectedTab === 'Decrease by new total FTE') {
    newTotalFTE = value;
  }
  setRequest({
    ...request,
    newNumberOfItems: newTotalFTE,
  });
};

export const getCostManipulatorToggleButtons = (requestType) => {
  if (requestType === 'increase') {
    return [
      BUDGETER_COST_MANIPULATOR_MODES.INCREASE_BY_COST,
      BUDGETER_COST_MANIPULATOR_MODES.INCREASE_BY_TOTAL,
    ];
  } else {
    return [
      BUDGETER_COST_MANIPULATOR_MODES.DECREASE_BY_COST,
      BUDGETER_COST_MANIPULATOR_MODES.DECREASE_BY_TOTAL,
    ];
  }
};

export const constructCostFieldTitle = (requestType, selectedTab) => {
  if (requestType === 'increase') {
    return selectedTab === BUDGETER_COST_MANIPULATOR_MODES.INCREASE_BY_COST ?
      BUDGETER_COST_MANIPULATOR_MODES.INCREASE_BY_COST
      : BUDGETER_COST_MANIPULATOR_MODES.INCREASE_BY_TOTAL;
  } else {
    return selectedTab === BUDGETER_COST_MANIPULATOR_MODES.DECREASE_BY_COST ?
      BUDGETER_COST_MANIPULATOR_MODES.DECREASE_BY_COST
      : BUDGETER_COST_MANIPULATOR_MODES.DECREASE_BY_TOTAL;
  }
};

export const determineCostFieldValue = (request, selectedTab) => {
  if (
    selectedTab === BUDGETER_COST_MANIPULATOR_MODES.INCREASE_BY_COST ||
    selectedTab === BUDGETER_COST_MANIPULATOR_MODES.DECREASE_BY_COST
  ) {
    const result =
      (
        getMoneyObj(request.newTotalCost).subtract(
          getMoneyObj(request.oldTotalCost),
        ).amount /
          100 <
        0
      ) ?
        Math.abs(Number(request.requestedAmount))
        : getMoneyObj(request.newTotalCost).subtract(
          getMoneyObj(request.oldTotalCost),
        ).amount / 100;
    return result;
  } else {
    return request.newTotalCost;
  }
};

export const setNewTotalCostAndRecalculateAllocations = (
  value,
  request,
  setRequest,
  requestType,
  selectedTab,
  flowTypeEdit,
) => {
  let newTotalCost;
  if (selectedTab === BUDGETER_COST_MANIPULATOR_MODES.INCREASE_BY_COST) {
    newTotalCost =
      getMoneyObj(request.oldTotalCost).add(getMoneyObj(value)).amount / 100;
  } else if (selectedTab === BUDGETER_COST_MANIPULATOR_MODES.DECREASE_BY_COST) {
    if (value > request.oldTotalCost) {
      newTotalCost = 0;
    } else {
      newTotalCost =
        getMoneyObj(request.oldTotalCost).subtract(getMoneyObj(value)).amount /
        100;
    }
  } else if (
    selectedTab === BUDGETER_COST_MANIPULATOR_MODES.INCREASE_BY_TOTAL
  ) {
    if (value <= request.oldTotalCost) {
      newTotalCost = request.oldTotalCost;
    } else {
      newTotalCost = value;
    }
  } else if (
    selectedTab === BUDGETER_COST_MANIPULATOR_MODES.DECREASE_BY_TOTAL
  ) {
    if (value >= request.oldTotalCost) {
      newTotalCost = request.oldTotalCost;
    } else {
      newTotalCost = value;
    }
  }

  const requestedAmount =
    getMoneyObj(newTotalCost).subtract(getMoneyObj(request.oldTotalCost))
      .amount / 100;
  setRequest({
    ...request,
    newTotalCost: newTotalCost,
    requestedAmount: requestedAmount,
    allocations: recalculateAllocations(
      request,
      newTotalCost,
      requestType,
      flowTypeEdit,
    ),
  });
};

export const recalculateAllocations = (
  request,
  newTotalCost,
  requestType,
  flowTypeEdit,
) => {
  if (flowTypeEdit) {
    if (requestType === REQUEST_TYPES_DB.INCREASE_ITEM_COST) {
      return resetAllocationsForIncreaseOnNewTotalCostChangeDuringEditMode(
        request.allocations,
        newTotalCost,
      );
    }

    if (requestType === REQUEST_TYPES_DB.DECREASE_ITEM_COST) {
      return resetAllocationsForDecreaseOnNewTotalCostChangeDuringEditMode(
        request.allocations,
        newTotalCost,
        request.oldTotalCost,
      );
    }
  } else {
    if (requestType === REQUEST_TYPES_DB.INCREASE_ITEM_COST) {
      return recalculateAllocationsProposedPercentagesForIncreaseRequest(
        request.originalAllocations,
        newTotalCost,
      );
    }

    if (requestType === REQUEST_TYPES_DB.DECREASE_ITEM_COST) {
      return recalculateAllocationsForDecreaseRequest(
        request.originalAllocations,
        newTotalCost,
        request.oldTotalCost,
      );
    }
  }

  return request.allocations;
};

export const resetAllocationsForIncreaseOnNewTotalCostChangeDuringEditMode = (
  initialAllocations,
  newTotalCost,
) => {
  const allocations = [
    ...initialAllocations.filter((allocation) => allocation.id),
  ];

  const currentProgramCosts = (allocations || []).map((allocation) =>
    Number(allocation.currentProgramCost),
  );
  const currentProgramCostsSumMoney = currentProgramCosts.reduce(
    (accumulator, cost) => {
      return accumulator.add(getMoneyObj(cost));
    },
    getMoneyObj(0),
  );
  const unallocatedSumMoney = getMoneyObj(newTotalCost || 0).subtract(
    currentProgramCostsSumMoney,
  );
  const proportionsArray = [
    ...currentProgramCosts,
    unallocatedSumMoney.amount / 100,
  ];

  const proposedPercentages = getMoneyObj(100).allocate(proportionsArray);

  allocations.forEach((allocation, i) => {
    allocation.proposedProgramCost = allocation.currentProgramCost;
    allocation.proposedPercentage = proposedPercentages[i].amount / 100;
    allocation.requestedAmount = 0;
  });

  return allocations;
};

export const resetAllocationsForDecreaseOnNewTotalCostChangeDuringEditMode = (
  initialAllocations,
  newTotalCost,
  oldTotalCost,
) => {
  const allocations = [
    ...initialAllocations.filter((allocation) => allocation.id),
  ];

  const currentProgramCosts = (allocations || []).map((allocation) =>
    Number(allocation.currentProgramCost),
  );
  const currentProgramCostsSumMoney = currentProgramCosts.reduce(
    (accumulator, cost) => {
      return accumulator.add(getMoneyObj(cost));
    },
    getMoneyObj(0),
  );
  const unallocatedSumMoney = getMoneyObj(oldTotalCost || 0).subtract(
    currentProgramCostsSumMoney,
  );
  const proportionsArray = [
    ...currentProgramCosts,
    unallocatedSumMoney.amount / 100,
  ];

  const proposedPercentages = getMoneyObj(100).allocate(proportionsArray);
  const proposedCosts = getMoneyObj(newTotalCost).allocate(proportionsArray);

  allocations.forEach((allocation, i) => {
    allocation.proposedProgramCost = proposedCosts[i].amount / 100;
    allocation.proposedPercentage = proposedPercentages[i].amount / 100;
    allocation.requestedAmount =
      proposedCosts[i].subtract(
        getMoneyObj(Number(allocation.currentProgramCost)),
      ).amount / 100;
  });

  return allocations;
};

export const recalculateAllocationsProposedPercentagesForIncreaseRequest = (
  initialAllocations,
  newTotalCost,
) => {
  const allocations = structuredClone(initialAllocations);
  const proposedProgramCosts = (allocations || []).map((allocation) =>
    Number(allocation.proposedProgramCost),
  );

  const proposedProgramCostsSumMoney = proposedProgramCosts.reduce(
    (accumulator, cost) => {
      return accumulator.add(getMoneyObj(cost));
    },
    getMoneyObj(0),
  );
  const unallocatedSumMoney = getMoneyObj(newTotalCost).subtract(
    proposedProgramCostsSumMoney,
  );
  const proportionsArray = [
    ...proposedProgramCosts,
    unallocatedSumMoney.amount / 100,
  ];

  const proposedPercentages = getMoneyObj(100).allocate(proportionsArray);

  allocations.forEach((allocation, i) => {
    allocation.proposedPercentage = proposedPercentages[i].amount / 100;
  });

  return allocations;
};

export const recalculateAllocationsForDecreaseRequest = (
  initialAllocations,
  newTotalCost,
  oldTotalCost,
) => {
  const allocations = structuredClone(initialAllocations);
  const currentProgramCosts = (allocations || []).map((allocation) =>
    Number(allocation.currentProgramCost),
  );

  const currentProgramCostsSumMoney = currentProgramCosts.reduce(
    (accumulator, cost) => {
      return accumulator.add(getMoneyObj(cost));
    },
    getMoneyObj(0),
  );
  const unallocatedSumMoney = getMoneyObj(oldTotalCost).subtract(
    currentProgramCostsSumMoney,
  );
  const proportionsArray = [
    ...currentProgramCosts,
    unallocatedSumMoney.amount / 100,
  ];

  const proposedPercentages = getMoneyObj(100).allocate(proportionsArray);
  const proposedCosts = getMoneyObj(newTotalCost).allocate(proportionsArray);

  allocations.forEach((allocation, i) => {
    allocation.proposedProgramCost = proposedCosts[i].amount / 100;
    allocation.proposedPercentage = proposedPercentages[i].amount / 100;
    allocation.requestedAmount =
      proposedCosts[i].subtract(
        getMoneyObj(Number(allocation.currentProgramCost)),
      ).amount / 100;
  });

  return allocations;
};

export const recalculateAllocationsForManualDecreaseRequest = (
  initialAllocations,
  newTotalCost,
) => {
  const allocations = structuredClone(initialAllocations);
  const proposedProgramCosts = (allocations || []).map((allocation) =>
    Number(allocation.proposedProgramCost),
  );
  const proposedProgramCostsSumMoney = proposedProgramCosts.reduce(
    (accumulator, cost) => {
      return accumulator.add(getMoneyObj(cost));
    },
    getMoneyObj(0),
  );
  const unallocatedSumMoney = getMoneyObj(newTotalCost).subtract(
    proposedProgramCostsSumMoney,
  );
  const proportionsArray = [
    ...proposedProgramCosts,
    unallocatedSumMoney.amount / 100,
  ];

  const proposedPercentages = getMoneyObj(100).allocate(proportionsArray);
  const proposedCosts = getMoneyObj(newTotalCost).allocate(proportionsArray);

  allocations.forEach((allocation, i) => {
    allocation.proposedProgramCost = proposedCosts[i].amount / 100;
    allocation.proposedPercentage = proposedPercentages[i].amount / 100;
  });

  return allocations;
};

export const resetAllocationsForDecreaseRequest = (
  initialAllocations,
  newTotalCost,
) => {
  const allocations = structuredClone(initialAllocations);
  const newTotalCostMoney = getMoneyObj(newTotalCost);

  allocations.forEach((allocation) => {
    allocation.proposedProgramCost = Number(allocation.currentProgramCost);
    allocation.proposedPercentage = getMoneyObj(
      allocation.proposedProgramCost,
    ).divide(newTotalCostMoney).amount;
    allocation.requestedAmount = 0;
  });

  return allocations;
};

export const resetAllocationsForIncreaseRequest = (
  initialAllocations,
  newTotalCost,
) => {
  const allocations = structuredClone(initialAllocations);

  const currentProgramCosts = (allocations || []).map((allocation) =>
    Number(allocation.currentProgramCost),
  );

  const currentProgramCostsSumMoney = currentProgramCosts.reduce(
    (accumulator, cost) => {
      return accumulator.add(getMoneyObj(cost));
    },
    getMoneyObj(0),
  );
  const unallocatedSumMoney = getMoneyObj(newTotalCost).subtract(
    currentProgramCostsSumMoney,
  );
  const proportionsArray = [
    ...currentProgramCosts,
    unallocatedSumMoney.amount / 100,
  ];

  const proposedPercentages = getMoneyObj(100).allocate(proportionsArray);

  allocations.forEach((allocation, i) => {
    allocation.proposedProgramCost = Number(allocation.currentProgramCost);
    allocation.proposedPercentage = proposedPercentages[i].amount / 100;
    allocation.requestedAmount = 0;
  });

  return allocations;
};

export const recalculateProposedPercentagesForNewItemRequest = (initialAllocations, newTotalCost) => {
  const allocations = structuredClone(initialAllocations);
  const newTotalCostMoney = getMoneyObj(Math.abs(newTotalCost));
  const newProgramCosts = (allocations || []).map(allocation => Math.abs(Number(allocation.proposedProgramCost)));
  const allocatedSumMoney = newProgramCosts.reduce((accumulator, cost) => {
    return accumulator.add(getMoneyObj(cost));
  }, getMoneyObj(0));
  const unallocatedSumMoney = newTotalCostMoney.subtract(allocatedSumMoney);
  const proportionsArray = [...newProgramCosts, unallocatedSumMoney.amount / 100].map(value => Math.abs(value));

  const newProposedPercentages = getMoneyObj(100).allocate(proportionsArray);

  allocations.forEach((allocation, i) => {
    allocation.proposedPercentage = newProposedPercentages[i].amount / 100;
  });

  return allocations;
};

export const getDisablingCondition = (
  request,
  activeStep,
  requestType,
  flowTypeEdit,
) => {
  if (!flowTypeEdit) {
    if (activeStep === 0 && request) {
      return createRequestStepZeroDisablingCondition(requestType, request);
    } else if (activeStep === 1) {
      return createRequestStepOneDisablingCondition(requestType, request);
    } else if (activeStep === 2) {
      const groupedBoxChoices = Object.entries(
        Object.groupBy(
          request.boxChoices,
          ({ boxChoiceTypeName }) => boxChoiceTypeName,
        ),
      );

      let result = groupedBoxChoices
        .filter((bc) => bc[1][0].required)
        .map((boxChoice) => {
          return boxChoice[1].map((choice) => {
            return choice.selected;
          });
        });
      const boxChoiceResult =
        result.length > 0 && result.some((r) => r.every((x) => x === false));
      const questionnaireResult = request.questionnaire.some(
        (q) => (q.required && q.answer.trim().length === 0) || q.invalid,
      );

      return boxChoiceResult || questionnaireResult;
    }
    return false;
  } else {
    const stepOneCondition =
      (
        requestType === REQUEST_TYPES_DB.INCREASE_ITEM_COST
          ?
          request.newTotalCost >= request.oldTotalCost
          : requestType === REQUEST_TYPES_DB.DECREASE_ITEM_COST ?
            request.newTotalCost <= request.oldTotalCost
            : request.newTotalCost) && request.isOffset != null && request.description.trim().length !== 0;
    const allocatedSumMoney = (request.allocations || []).reduce(
      (total, allocation) => {
        return total.add(
          getMoneyObj(Math.abs(Number(allocation.requestedAmount))),
        );
      },
      getMoneyObj(0),
    );

    const stepTwoCondition = allocatedSumMoney.equals(
      getMoneyObj(Math.abs(Number(request.requestedAmount))),
    );

    const groupedBoxChoices = Object.entries(
      Object.groupBy(
        request.boxChoices,
        ({ boxChoiceTypeName }) => boxChoiceTypeName,
      ),
    );

    let result = groupedBoxChoices
      .filter((bc) => bc[1][0].required)
      .map((boxChoice) => {
        return boxChoice[1].map((choice) => {
          return choice.selected;
        });
      });
    const boxChoiceResult =
      result.length > 0 && result.some((r) => r.every((x) => x === false));
    const questionnaireResult = request.questionnaire.some(
      (q) => (q.required && q.answer.trim().length === 0) || q.invalid,
    );

    const stepThreeCondition = !boxChoiceResult && !questionnaireResult;
    return stepOneCondition && stepTwoCondition && stepThreeCondition;
  }
};

export const getDisablingConditionTooltip = (
  request,
  activeStep,
  requestType,
  nameConventions
) => {
  if (!request) {
    return '';
  }

  const messages = messageManager(nameConventions);

  if (activeStep === 0) {
    if (
      (requestType === REQUEST_TYPES_DB.INCREASE_ITEM_COST &&
        request.newTotalCost <= request.oldTotalCost) ||
      (requestType === REQUEST_TYPES_DB.DECREASE_ITEM_COST &&
        request.newTotalCost >= request.oldTotalCost) ||
      !request.newTotalCost
    ) {
      return messages.BUDGET_REQUESTS.REQUESTER.ADJUST_REQUESTED_AMOUNT_TOOLTIP;
    }

    if (request.isOffset == null) {
      return messages.BUDGET_REQUESTS.REQUESTER.MISSING_OFFSET_TOOLTIP;
    }

    if (request.description.trim().length === 0) {
      return messages.BUDGET_REQUESTS.REQUESTER.MISSING_DESCRIPTION_TOOLTIP;
    }
  }

  return '';
};

export const getNewItemFields = (
  request,
  optionsObject = [],
  costCenters = [],
  allowCustomValues = false,
  nameConventions
) => {
  let organizationLevelFields = generateOrganizationLevelFieldsWithDropDowns(
    request.itemData.organizationLevels,
    optionsObject.filter(
      (obj) => obj.type === FILTER_TYPES.BUDGETER_ORGANIZATION_LEVELS,
    )[0],
    allowCustomValues
  );

  let objectCategoryFields = generateObjectCategoryFieldsWithDropDowns(
    request.itemData.objectCategories,
    optionsObject.filter(
      (obj) =>
        obj.type === FILTER_TYPES.BUDGETER_PERSONNEL ||
        obj.type === FILTER_TYPES.BUDGETER_NON_PERSONNEL,
    ),
    allowCustomValues
  );

  const getAccountTypes = () => {
    const accountTypes = optionsObject
      .find((oo) => oo.type === FILTER_TYPES.ACCOUNT_TYPE)
      .options.map((def) => {
        return { label: def.name, value: def };
      });
      
    return request.itemData.objectType === PERSONNEL ? accountTypes.filter(
      at=> at.label === EXPENSE) : accountTypes;
  };

  return [
    {
      label: 'AcctCode',
      propPath: 'itemData.accountCode',
      type: 'text',
      fieldType: 'main',
    },
    {
      label: 'Account type',
      propPath: 'itemData.accountType',
      idPropPath: 'itemData.accountTypeId',
      required: true,
      type: 'autocomplete',
      fieldType: 'main',
      options: getAccountTypes(),
    },
    {
      label: 'Duration type',
      propPath: 'itemData.durationType',
      idPropPath: 'itemData.durationTypeId',
      required: true,
      type: 'autocomplete',
      fieldType: 'main',
      options: optionsObject
        .find((oo) => oo.type === 'DURATION_TYPE')
        .options.map((def) => {
          return { label: def.name, value: def };
        }),
    },
    {
      label: 'Total item cost',
      propPath: 'newTotalCost',
      fieldType: 'main',
      type: 'number',
      prefix: '$',
      required: true,
    },
    {
      label: 'Number of items',
      propPath: 'newNumberOfItems',
      fieldType: 'main',
      prefix: '',
      type: 'number',
    },
    {
      label: 'Fund',
      propPath: 'itemData.fund',
      idPropPath: 'itemData.fundId',
      required: true,
      fieldType: 'main',
      type: 'autocomplete',
      options: optionsObject
        .find((oo) => oo.type === 'FUND')
        .options.map((def) => {
          return { label: def.name, value: def };
        }),
    },
    ...organizationLevelFields,
    ...objectCategoryFields,
    {
      label: `${nameConventions[USER_GROUP].alias}`,
      propPath: 'itemData.costCenter',
      idPropPath: 'itemData.costCenterId',
      required: true,
      fieldType: 'main',
      type: 'autocomplete',
      options:
        costCenters.map((cc) => {
          return { label: cc.name, value: cc };
        }) || [],
    },
    {
      label: 'Item meta 1',
      propPath: 'itemData.itemMeta1',
      type: 'text',
      fieldType: 'additional',
    },
    {
      label: 'Item meta 2',
      propPath: 'itemData.itemMeta2',
      type: 'text',
      fieldType: 'additional',
    },
    {
      label: 'Item meta 3',
      propPath: 'itemData.itemMeta3',
      type: 'text',
      fieldType: 'additional',
    },
    {
      label: 'Name meta',
      propPath: 'itemData.nameMeta',
      type: 'text',
      fieldType: 'additional',
    },
    {
      label: 'Comments',
      propPath: 'itemData.comments',
      type: 'text',
      fieldType: 'additional',
    },
  ];
};

const generateOrganizationLevelFieldsWithDropDowns = (
  organizationLevels,
  optionsObject,
  allowCustomValues,
) => {
  if (allowCustomValues) {
    return organizationLevels.map((ol, i) => {
      return {
        label: ol.typeName,
        propPath: `itemData.organizationLevels[${i}].name`,
        idPropPath: `itemData.organizationLevels[${i}].organizationLevelTypeId`,
        fieldType: 'main',
        type: 'text',
      };
    });
  }

  return organizationLevels.map((ol, i) => {
    return {
      label: ol.typeName,
      propPath: `itemData.organizationLevels[${i}].name`,
      idPropPath: `itemData.organizationLevels[${i}].organizationLevelTypeId`,
      fieldType: 'main',
      type: 'autocomplete',
      options:
        optionsObject?.options
          .find((oo) => ol.typeName === oo.name)
          ?.definitions.map((def) => {
            return { label: def.name, value: { ...def, id: def.level_id } }; //ridiculous!!! name the id prop to be actually an level id so we can auto map everything
          }) || [],
    };
  });
};

const generateObjectCategoryFieldsWithDropDowns = (
  objectCategories,
  optionsObject,
  allowCustomValues,
) => {
  if (allowCustomValues) {
    return objectCategories.map((oc, i) => {
      return {
        label: oc.typeName,
        propPath: `itemData.objectCategories[${i}].name`,
        idPropPath: `itemData.objectCategories[${i}].objectCategoryTypeId`,
        fieldType: 'main',
        type: 'text',
      };
    });
  }

  return objectCategories.map((oc, i) => {
    return {
      label: oc.typeName,
      propPath: `itemData.objectCategories[${i}].name`,
      idPropPath: `itemData.objectCategories[${i}].objectCategoryTypeId`,
      fieldType: 'main',
      type: 'autocomplete',
      options:
        optionsObject
          .map((oo) => oo.options)
          .flat()
          .find((oo) => oc.typeName === oo.name && oc.objectCategoryTypeId === oo.id)
          ?.definitions.map((def) => {
            return { label: def.name, value: { ...def, id: def.type_id } }; // even more ridiculous, since the levels have level_id but the categories have type_id
          }) || [],
    };
  });
};

export const getNewItemSummaryFields = (request) => {
  let organizationLevelFields = generateOrganizationLevelFields(
    request.itemData.organizationLevels,
  );

  let objectCategoryFields = generateObjectCategoryFields(
    request.itemData.objectCategories,
  );

  return [
    {
      label: 'Type of request',
      propPath: 'rawType',
      type: FIELD_TYPE_STRING,
    },
    organizationLevelFields[0],
    {
      label: 'Cost type',
      propPath: 'itemData.objectType',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'Item duration',
      propPath: 'itemData.durationType',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'AcctCode',
      propPath: 'itemData.accountCode',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'Account type',
      propPath: 'itemData.accountType',
      type: FIELD_TYPE_STRING,
    },
    {
      label: 'Total item cost',
      propPath: 'oldTotalCost',
      type: FIELD_TYPE_MONEY,
    },
    {
      label: 'New total item cost',
      propPath: 'newTotalCost',
      type: FIELD_TYPE_MONEY,
    },
    {
      label: 'Old number of items',
      propPath: 'oldNumberOfItems',
      type: FIELD_TYPE_NUMBER,
    },
    {
      label: 'New number of items',
      propPath: 'newNumberOfItems',
      type: FIELD_TYPE_NUMBER,
    },
    {
      label: 'Fund',
      propPath: 'itemData.fund',
      fieldType: 'main',
      type: FIELD_TYPE_STRING,
    },
    ...organizationLevelFields.filter(
      (ol) => ol.propPath !== 'itemData.organizationLevels[0].name',
    ),
    ...objectCategoryFields,
  ];
};

export const updateBoxChoices = (boxChoices, value) => {
  let updatedBoxChoices = [];
  if (value.type === BOX_CHOICE_TYPES.SINGLE_CHOICE) {
    updatedBoxChoices = boxChoices.map((choice) => {
      if (
        choice.id === value.id &&
        choice.boxChoiceTypeId === value.boxChoiceTypeId
      ) {
        return { ...choice, selected: true };
      } else if (
        choice.id !== value.id &&
        choice.boxChoiceTypeId === value.boxChoiceTypeId
      ) {
        return { ...choice, selected: false };
      } else {
        return { ...choice };
      }
    });
  } else {
    updatedBoxChoices = boxChoices.map((choice) => {
      if (choice.id === value.id) {
        return { ...choice, selected: !choice.selected };
      } else {
        return { ...choice };
      }
    });
  }
  return updatedBoxChoices;
};

export const handleFileManipulation = (
  e,
  question,
  setUploadedFiles,
  setRequest,
) => {
  if (e.detail?.legalFiles?.length > 0) {
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedFiles((prevState) => ({
        ...prevState,
        [question.questionId]: {
          answerId: question.answerId,
          questionId: question.questionId,
          fileName: e.detail.legalFiles[0].name,
          name: e.detail.legalFiles[0].name,
          fileUpload: event.target.result,
          fileLink: URL.createObjectURL(e.detail.legalFiles[0]),
        },
      }));
      setRequest((prevState) => ({
        ...prevState,
        questionnaire: prevState.questionnaire.map((q) =>
          q.questionId === question.questionId ?
            {
              ...q,
              answer: e.detail.legalFiles[0].name,
              invalid: getDuplicateFileCondition(prevState, {
                answer: e.detail.legalFiles[0].name,
                questionId: question.questionId,
              }),
            }
            : q,
        ),
      }));
    };
    reader.readAsDataURL(e.detail.legalFiles[0]);
  } else {
    // remove that file from uploadedFiles
    setUploadedFiles((prevState) => {
      const { [question.questionId]: _, ...newState } = prevState;
      return newState;
    });
    setRequest((prevState) => ({
      ...prevState,
      questionnaire: prevState.questionnaire.map((q) =>
        q.questionId === question.questionId ? { ...q, answer: '' } : q,
      ),
    }));
  }
};

export const getDuplicateFileCondition = (request, question) => {
  return request.questionnaire
    .filter(
      (x) =>
        x.type === 'file' &&
        x.answer.trim().length !== 0 &&
        x.questionId !== question.questionId,
    )
    .map((x) => x.answer)
    .includes(question.answer);
};

export const getAllocationTableColumns = (nameConventions) => {
  return [
    {
      colId: 'programDepartment',
      headerName: nameConventions[PROGRAM_DEPARTMENT].alias,
      field: 'department',
      headerTooltip: nameConventions[PROGRAM_DEPARTMENT].alias,
      tooltipField: 'department',
      width: TABLE_COLUMN_WIDTH.XL,
    },
    {
      colId: 'programDivision',
      headerName: nameConventions[PROGRAM_DIVISION].alias,
      field: 'division',
      headerTooltip: nameConventions[PROGRAM_DIVISION].alias,
      tooltipField: 'division',
      aggFunc: () => 'Total',
      width: TABLE_COLUMN_WIDTH.XL
    },
    {
      colId: 'program',
      headerName: nameConventions[PROGRAM].alias,
      field: 'program',
      headerTooltip: nameConventions[PROGRAM].alias,
      tooltipField: 'program',
      width: TABLE_COLUMN_WIDTH.DEFAULT
    },
    {
      colId: 'currentPercentage',
      headerName: 'Current percentage',
      field: 'currentPercentage',
      headerTooltip: 'Current percentage',
      tooltipValueGetter: (params) => {
        return percentageFormat(params.value);
      },
      valueFormatter: (params) => {
        return percentageFormat(params.value);
      },
      aggFunc: customAggregateFunction,
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
      width: TABLE_COLUMN_WIDTH.LG
    },
    {
      colId: 'currentAmount',
      headerName: 'Current amount',
      field: 'currentProgramCost',
      headerTooltip: 'Current amount',
      tooltipValueGetter: (params) => {
        return totalCostFormat(Number(params.value));
      },
      valueFormatter: (params) => {
        return totalCostFormat(Number(params.value));
      },
      aggFunc: customAggregateFunction,
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
      width: TABLE_COLUMN_WIDTH.LG
    },
    {
      colId: 'requestedAmount',
      headerName: 'Request amount',
      field: 'requestedAmount',
      headerTooltip: 'Request amount',
      tooltipValueGetter: (params) => {
        return totalCostFormat(Number(params.value));
      },
      valueFormatter: (params) => {
        return totalCostFormat(Number(params.value));
      },
      aggFunc: customAggregateFunction,
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
      width: TABLE_COLUMN_WIDTH.LG
    },
    {
      colId: 'proposedPercentage',
      headerName: 'Proposed percentage',
      field: 'proposedPercentage',
      headerTooltip: 'Proposed percentage',
      tooltipValueGetter: (params) => {
        return percentageFormat(params.value);
      },
      valueFormatter: (params) => {
        return percentageFormat(params.value);
      },
      aggFunc: customAggregateFunction,
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
      width: TABLE_COLUMN_WIDTH.LG
    },
    {
      colId: 'proposedAmount',
      headerName: 'Proposed amount',
      field: 'proposedProgramCost',
      headerTooltip: 'Proposed amount',
      tooltipValueGetter: (params) => {
        return totalCostFormat(Number(params.value));
      },
      valueFormatter: (params) => {
        return totalCostFormat(Number(params.value));
      },
      aggFunc: customAggregateFunction,
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
      width: TABLE_COLUMN_WIDTH.LG
    },
  ];
};

export const autoAllocate = (request) => {
  const { requestedAmount, type, originalAllocations, oldTotalCost, newTotalCost } = request;

  switch (type) {
    case REQUEST_TYPES_DB.DECREASE_ITEM_COST:
      return decreaseAutoAllocate(originalAllocations, oldTotalCost, newTotalCost);
    case REQUEST_TYPES_DB.INCREASE_ITEM_COST:
      return increaseAutoAllocate(originalAllocations, oldTotalCost, newTotalCost);
    case REQUEST_TYPES_DB.CREATE_NEW_ITEM_FROM_EXISTING:
      return addExistingAutoAllocate(originalAllocations, requestedAmount);
  }
};

const decreaseAutoAllocate = (originalAllocations, oldTotalCost, newTotalCost) => {
  const allocations = structuredClone(originalAllocations);
  const currentProgramCosts = (allocations || []).map((allocation) =>
    Number(allocation.currentProgramCost),
  );

  const currentProgramCostsSumMoney = currentProgramCosts.reduce(
    (accumulator, cost) => {
      return accumulator.add(getMoneyObj(cost));
    },
    getMoneyObj(0),
  );

  const unallocatedSumMoney = getMoneyObj(oldTotalCost).subtract(
    currentProgramCostsSumMoney,
  );

  const proportionsArray = [
    ...currentProgramCosts,
    unallocatedSumMoney.amount / 100,
  ];

  const proposedPercentages = getMoneyObj(100).allocate(proportionsArray);
  const proposedCosts = getMoneyObj(newTotalCost).allocate(proportionsArray);

  allocations.forEach((allocation, i) => {
    allocation.proposedProgramCost = proposedCosts[i].amount / 100;
    allocation.proposedPercentage = proposedPercentages[i].amount / 100;
    allocation.requestedAmount =
      proposedCosts[i].subtract(
        getMoneyObj(Number(allocation.currentProgramCost)),
      ).amount / 100;
  });

  return allocations;
};
const increaseAutoAllocate = (originalAllocations, oldTotalCost, newTotalCost) => {
  const allocations = structuredClone(originalAllocations);
  const currentProgramCosts = (allocations || []).map(a => Number(a.currentProgramCost));
  const currentProgramCostsSumMoney = currentProgramCosts.reduce(
    (sum, cost) => {
      return sum.add(getMoneyObj(cost));
    },
    getMoneyObj(0)
  );
  const unallocatedSumMoney = getMoneyObj(oldTotalCost).subtract(currentProgramCostsSumMoney);
  const proportionsArray = [...currentProgramCosts, unallocatedSumMoney.amount / 100].map(value => Math.abs(value));

  const proposedProgramCosts = getMoneyObj(newTotalCost).allocate(proportionsArray);
  const proposedPercentages = getMoneyObj(100).allocate(proposedProgramCosts.map(money => Math.abs(money.amount)));

  allocations.forEach((allocation, i) => {
    allocation.proposedProgramCost = Number((proposedProgramCosts[i].amount / 100).toFixed(2));
    allocation.proposedPercentage = Number((proposedPercentages[i].amount / 100).toFixed(2));
    allocation.requestedAmount = Number(((proposedProgramCosts[i].subtract(getMoneyObj(Number(allocation.currentProgramCost)))).amount / 100).toFixed(2));
  });

  return allocations;
};

const addExistingAutoAllocate = (allocations, requestedAmount) => {
  const programCosts = allocations.map((x) => x.proposedProgramCost);
  const allocatedCosts = getMoneyObj(requestedAmount).allocate(
    programCosts.map((x) => x * 100),
  );

  return allocations.map((allocation, index) => {
    return {
      ...allocation,
      proposedProgramCost: allocatedCosts[index].amount / 100,
      proposedPercentage: allocation.currentPercentage,
      requestedAmount: allocatedCosts[index].amount / 100,
      currentPercentage: 0,
      currentProgramCost: 0,
    };
  });
};

export const autoAllocateNewItem = (request) => {
  if ((request.originalAllocations || []).length === 0) {
    request.allocations.forEach((allocation) => {
      allocation.currentPercentage = 0;
      allocation.currentProgramCost = 0;
      allocation.proposedProgramCost = 0;
      allocation.proposedPercentage = 0;
      allocation.requestedAmount = 0;
    });

    return;
  }

  autoAllocateNewItemWithExistingAllocations(request);
};

const autoAllocateNewItemWithExistingAllocations = (request) => {
  request.allocations = structuredClone(request.originalAllocations || []);
  const oldProgramCosts = (request.allocations || []).map((allocation) =>
    Number(allocation.currentProgramCost),
  );
  const oldAllocatedSumMoney = oldProgramCosts.reduce((accumulator, cost) => {
    return accumulator.add(getMoneyObj(cost));
  }, getMoneyObj(0));

  const oldUnallocatedSumMoney = getMoneyObj(
    request.originalTotalCost,
  ).subtract(oldAllocatedSumMoney);
  const proportionsArray = [
    ...oldProgramCosts,
    Number((oldUnallocatedSumMoney.amount / 100).toFixed(2)),
  ];

  const money100 = getMoneyObj(100);
  const newPercentages = money100.allocate(proportionsArray);
  const newAllocations = getMoneyObj(request.newTotalCost).allocate(
    newPercentages.map((percent) => percent.amount),
  );

  request.allocations.forEach((allocation, i) => {
    allocation.currentPercentage = 0;
    allocation.currentProgramCost = 0;
    allocation.proposedProgramCost = newAllocations[i].amount / 100;
    allocation.proposedPercentage = newPercentages[i].amount / 100;
    allocation.requestedAmount = newAllocations[i].amount / 100;
  });
};


// EXPERIMENTAL: 2026-02-11
// attempt to recalculate a new item request based on newly added allocations
/* const bootstrapAutoAllocateNewItem = (request) => {
  request.allocations = structuredClone(request.allocations || []);
  const oldProgramCosts = (request.allocations || []).map((allocation) =>
    Number(allocation.proposedProgramCost),
  );
  const oldAllocatedSumMoney = oldProgramCosts.reduce((accumulator, cost) => {
    return accumulator.add(getMoneyObj(cost));
  }, getMoneyObj(0));

  const oldUnallocatedSumMoney = getMoneyObj(
    request.newTotalCost,
  ).subtract(oldAllocatedSumMoney);
  const proportionsArray = [
    ...oldProgramCosts,
    Number((oldUnallocatedSumMoney.amount / 100).toFixed(2)),
  ];

  const money100 = getMoneyObj(100);
  const newPercentages = money100.allocate(proportionsArray);
  const newAllocations = getMoneyObj(request.newTotalCost).allocate(
    newPercentages.map((percent) => percent.amount),
  );

  request.allocations.forEach((allocation, i) => {
    allocation.currentPercentage = 0;
    allocation.currentProgramCost = 0;
    allocation.proposedProgramCost = newAllocations[i].amount / 100;
    allocation.proposedPercentage = newPercentages[i].amount / 100;
    allocation.requestedAmount = newAllocations[i].amount / 100;
  });
}; */

export const manualAllocationFields = (nameConventions) => {

  return [
    {
      name: `${nameConventions[PROGRAM_DEPARTMENT].alias}`,
      id: 1,
      options: [],
      type: 'autocomplete',
    },
    {
      name: `${nameConventions[PROGRAM_DIVISION].alias}`,
      id: 2,
      options: [],
      type: 'autocomplete',
    },
    {
      name: `${nameConventions[PROGRAM].alias}`,
      id: 3,
      options: [],
      type: 'autocomplete',
    },
    {
      name: `Additional ${nameConventions[PROGRAM].alias} cost`,
      id: 4,
      type: 'numeric',
    },
  ];
};

export const calculateRemainingAmount = (request, requestType) => {
  const allocationRequestedAmountSum = request.allocations.reduce(
    (sum, allocation) => {
      return sum.add(getMoneyObj(Math.abs(allocation.requestedAmount) || 0));
    },
    getMoneyObj(0),
  );
  const remainingAmount =
    requestType !== REQUEST_TYPES_DB.DECREASE_ITEM_COST ?
      getMoneyObj(request.requestedAmount).subtract(
        allocationRequestedAmountSum,
      )
      : getMoneyObj(request.requestedAmount).add(allocationRequestedAmountSum);
  return remainingAmount.amount / 100;
};

export const sortDropDowns = (arr) => arr.sort((a, b) => a.localeCompare(b));

export const attachUploadedFiles = (formData, files) => {
  Object.values(files).forEach((file) => {
    if (file.answerId) {
      return;
    }
    const uploadedFile = file.fileUpload;

    const [meta, base64Data] = uploadedFile.split(',');
    const mimeType = meta.match(/data:(.*);base64/)[1];

    const byteChars = atob(base64Data);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    formData.append('files', blob, file.fileName);
  });
};
